/**
 * 预算管理（延伸需求 3.6）
 *
 * 编制、调整、执行进度。
 *
 * 【预算不维护"已用"计数器】`已用 / 在途 / 当前预算` 全是服务端读时聚合的结果：
 *   当前预算 = 原始核定金额 + 已批准的调整
 *   已用     = 已通过及之后的报销明细汇总
 *   在途     = 审批中的报销 + 已批准但尚未报销完毕的申请
 * 存一个"已用"列就得在明细增改删、状态迁移、跨期间调整每条路径上同步，
 * 而现实永远只维护其中一条（本仓库为这类计数器付过代价，见 013 迁移）。
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Empty,
  Table,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Card,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  BudgetPeriodType,
  BUDGET_PERIOD_TYPE_LABELS,
  BudgetStatus,
  BUDGET_STATUS_LABELS,
  ExpenseType,
  EXPENSE_TYPE_LABELS,
  PermissionCode,
  RiskLevel,
} from '@ilc/shared';
import type { ExpenseBudget } from '@ilc/shared';
import { expenseApi, orgApi } from '@/api/endpoints';
import { usePagedList } from '@/hooks/usePagedList';
import { useResource } from '@/hooks/useResource';
import { useAuthStore } from '@/stores/auth';
import { DataTable } from '@/components/DataTable';
import { PageHeader } from '@/components/PageHeader';
import { Money, moneyCellStyle } from '@/components/Money';
import { ConfirmDanger } from '@/components/ConfirmDanger';
import { feedback } from '@/utils/feedback';
import { performAction } from '@/utils/performAction';

interface Query {
  page?: number;
  pageSize?: number;
  period?: string;
  periodType?: BudgetPeriodType;
  expenseType?: ExpenseType;
  status?: BudgetStatus;
}

/**
 * 缺省期间：当前期间，格式与后端 `normalizePeriod` 严格一致
 * （`2026-09` / `2026Q3` / `2026`）。对不上会出现"预算有、执行算出 0"。
 *
 * 季度用月份算，不引 dayjs 的 quarter 插件——为一个除法装一个插件不划算。
 */
function currentPeriod(t: BudgetPeriodType): string {
  const d = dayjs();
  if (t === 'MONTH') return d.format('YYYY-MM');
  if (t === 'QUARTER') return `${d.format('YYYY')}Q${Math.floor(d.month() / 3) + 1}`;
  return d.format('YYYY');
}

export function ExpenseBudgetPage(): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const canManage = can(PermissionCode.EXPENSE_BUDGET_MANAGE);

  const [periodType, setPeriodType] = useState<BudgetPeriodType>(BudgetPeriodType.MONTH);
  const list = usePagedList<ExpenseBudget, Query>({
    loader: (q) => expenseApi.listBudgets(q),
    /** 默认按月 + 当月：不筛期间的执行率没有意义（跨期间求和是解释不清的数字） */
    initialQuery: {
      page: 1,
      pageSize: 20,
      periodType: BudgetPeriodType.MONTH,
      period: currentPeriod(BudgetPeriodType.MONTH),
    },
  });

  const depts = useResource(() => orgApi.depts(), { deps: [] });

  const [createModal, setCreateModal] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState<ExpenseBudget | null>(null);
  const [adjustListTarget, setAdjustListTarget] = useState<ExpenseBudget | null>(null);
  /**
   * 调整记录：**只有打开某条预算的记录时才拉**（`enabled` 控制）。
   * 预算列表可能几十条，预加载每条的调整记录是几十次没必要的查询。
   */
  const adjusts = useResource(
    () => (adjustListTarget ? expenseApi.budgetAdjusts(adjustListTarget.id) : Promise.resolve([])),
    { deps: [adjustListTarget?.id], enabled: adjustListTarget !== null },
  );
  const [deleteTarget, setDeleteTarget] = useState<ExpenseBudget | null>(null);
  const [saving, setSaving] = useState(false);
  const [createForm] = Form.useForm();
  const [adjustForm] = Form.useForm();

  const totals = useMemo(() => {
    const rows = list.list;
    return {
      current: rows.reduce((s, r) => s + r.currentCents, 0),
      used: rows.reduce((s, r) => s + r.usedCents, 0),
      pending: rows.reduce((s, r) => s + r.pendingCents, 0),
      amount: rows.reduce((s, r) => s + r.amountCents, 0),
    };
  }, [list.list]);

  const execRatio = totals.current > 0 ? (totals.used + totals.pending) / totals.current : null;

  const columns = [
    { title: '期间', dataIndex: 'period', width: 90, render: (v: string) => <span className="ilc-mono">{v}</span> },
    {
      title: '周期',
      dataIndex: 'periodType',
      width: 70,
      render: (v: string) => BUDGET_PERIOD_TYPE_LABELS[v as BudgetPeriodType] ?? v,
    },
    { title: '部门', dataIndex: 'deptName', width: 100, render: (v: string | null) => v ?? '全公司' },
    { title: '工程', dataIndex: 'projectName', width: 120, render: (v: string | null) => v ?? '不限' },
    {
      title: '费用类型',
      dataIndex: 'expenseType',
      width: 100,
      render: (v: string) => EXPENSE_TYPE_LABELS[v as ExpenseType] ?? v,
    },
    {
      title: '原始核定',
      dataIndex: 'amountCents',
      width: 120,
      align: 'right' as const,
      onCell: () => ({ style: moneyCellStyle }),
      render: (v: number) => <Money cents={v} />,
    },
    {
      title: '已批准调整',
      dataIndex: 'adjustCents',
      width: 110,
      align: 'right' as const,
      onCell: () => ({ style: moneyCellStyle }),
      render: (v: number) =>
        v === 0 ? (
          <span style={{ color: 'rgba(0,0,0,0.35)' }}>-</span>
        ) : (
          <span style={{ color: v < 0 ? '#cf1322' : '#389e0d' }}>
            <Money cents={v} colorNegative={false} />
          </span>
        ),
    },
    {
      title: '当前预算',
      dataIndex: 'currentCents',
      width: 120,
      align: 'right' as const,
      onCell: () => ({ style: moneyCellStyle }),
      render: (v: number) => <Money cents={v} strong />,
    },
    {
      title: '已用',
      dataIndex: 'usedCents',
      width: 120,
      align: 'right' as const,
      onCell: () => ({ style: moneyCellStyle }),
      render: (v: number) => <Money cents={v} />,
    },
    {
      title: '在途',
      dataIndex: 'pendingCents',
      width: 120,
      align: 'right' as const,
      onCell: () => ({ style: moneyCellStyle }),
      render: (v: number) => <Money cents={v} />,
    },
    {
      title: '执行率',
      dataIndex: 'usedRatio',
      width: 160,
      render: (v: number | null) =>
        v === null ? (
          // 当前预算为 0 时执行率无法定义——渲染成「-」而不是 0%
          <span style={{ color: 'rgba(0,0,0,0.35)' }}>-</span>
        ) : (
          <Space size={4}>
            <Progress
              percent={Math.min(100, Math.round(v * 100))}
              size="small"
              status={v > 1 ? 'exception' : v >= 0.8 ? 'active' : 'normal'}
              style={{ width: 80 }}
            />
            {v > 1 && <Tag color="error">超支</Tag>}
          </Space>
        ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (v: string) => (
        <Tag color={v === BudgetStatus.ACTIVE ? 'success' : 'default'}>
          {BUDGET_STATUS_LABELS[v as BudgetStatus] ?? v}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: unknown, r: ExpenseBudget) => (
        <Space size={4}>
          {canManage && r.status === BudgetStatus.ACTIVE && (
            <>
              <Button size="small" type="link" onClick={() => setAdjustTarget(r)}>
                调整
              </Button>
              <Button size="small" type="link" onClick={() => setAdjustListTarget(r)}>
                调整记录
              </Button>
            </>
          )}
          {canManage && (
            <Button size="small" type="link" danger onClick={() => setDeleteTarget(r)}>
              删除
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const saveCreate = async (): Promise<void> => {
    const v = await createForm.validateFields();
    setSaving(true);
    try {
      const pt = v.periodType as BudgetPeriodType;
      const result = await performAction({
        action: () =>
          expenseApi.createBudget({
            period: v.period ? dayjs(v.period).format(pt === 'MONTH' ? 'YYYY-MM' : 'YYYY') : currentPeriod(pt),
            periodType: pt,
            deptId: v.deptId ?? null,
            expenseType: v.expenseType,
            projectId: null,
            amountCents: Math.round(Number(v.amountYuan ?? 0) * 100),
            remark: v.remark || null,
          }),
        successMessage: '预算已建',
        offline: () => undefined,
        offlineMessage: '后端预算接口尚未就绪，本次保存仅在本地演示',
      });
      if (result.ok) {
        setCreateModal(false);
        createForm.resetFields();
        list.reload();
      }
    } finally {
      setSaving(false);
    }
  };

  const saveAdjust = async (): Promise<void> => {
    if (!adjustTarget) return;
    const v = await adjustForm.validateFields();
    setSaving(true);
    try {
      const result = await performAction({
        action: () =>
          expenseApi.createBudgetAdjust(adjustTarget.id, {
            deltaCents: Math.round(Number(v.deltaYuan ?? 0) * 100),
            reason: v.reason,
          }),
        successMessage: '调整已提交，待批准后生效',
        offline: () => undefined,
        offlineMessage: '后端接口尚未就绪，本次提交仅在本地演示',
      });
      if (result.ok) {
        setAdjustTarget(null);
        adjustForm.resetFields();
        feedback.info(
          '预算调整需要审批：未批准的调整不计入当前预算。批完后到「批调整」里用调整单号确认。',
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const saveDelete = async (reason: string): Promise<void> => {
    if (!deleteTarget) return;
    const result = await performAction({
      action: () => expenseApi.removeBudget(deleteTarget.id),
      successMessage: '预算已删除',
      offline: () => undefined,
      offlineMessage: '后端接口尚未就绪，本次删除仅在本地演示',
    });
    if (result.ok) {
      feedback.info(`删除说明：${reason}`);
      setDeleteTarget(null);
      list.reload();
    } else {
      setDeleteTarget(null);
      list.reload();
    }
  };

  /** 批准/驳回一条调整。冲突（已被别人处理）时提示刷新，不当成失败 */
  const decideAdjustment = async (adjustId: string, approved: boolean): Promise<void> => {
    if (!adjustListTarget) return;
    try {
      await expenseApi.decideBudgetAdjust(adjustListTarget.id, adjustId, {
        approved,
        note: approved ? '批准' : '驳回',
      });
      feedback.success(approved ? '调整已批准，预算已更新' : '调整已驳回');
      adjusts.reload();
      list.reload();
    } catch (err) {
      feedback.error(err instanceof Error ? err.message : '处理失败');
      adjusts.reload();
      list.reload();
    }
  };

  return (
    <div className="ilc-page">
      <PageHeader
        title="预算管理"
        description="按期间 × 部门 × 费用类型编制；执行情况实时汇总"
        extra={
          <Space>
            {canManage && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModal(true)}>
                编制预算
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={list.reload}>
              刷新
            </Button>
          </Space>
        }
      />

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="本页当前预算合计" value={totals.current / 100} precision={2} prefix="¥" />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="已用" value={totals.used / 100} precision={2} prefix="¥" />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="在途" value={totals.pending / 100} precision={2} prefix="¥" />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="执行率"
              value={execRatio === null ? undefined : Math.round(execRatio * 100)}
              suffix={execRatio === null ? undefined : '%'}
              valueStyle={{ color: execRatio !== null && execRatio > 1 ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="当前预算 = 原始核定金额 + 已批准的调整；执行率 =（已用 + 在途）/ 当前预算"
        description={
          <span>
            <strong>为什么在途也算</strong>：只看已批准的话，预算在审批期间会显得很宽裕，
            等一批在途单同时批下来才发现超了，那时已经拦不住。
            <br />
            <strong>已发生支出的预算不能直接改金额，也不能删</strong>——改了会把历史支出与新预算放一起比，
            执行率突然失真。要撤销请走「调整」并把它调减到 0，那样"为什么取消"才留得下判据。
          </span>
        }
      />

      <DataTable<ExpenseBudget>
        storageKey="expense-budget"
        exportFileName="预算执行表"
        exportColumns={[
          { title: '期间', dataIndex: 'period' },
          { title: '周期', value: (r) => BUDGET_PERIOD_TYPE_LABELS[r.periodType] ?? r.periodType },
          { title: '部门', value: (r) => r.deptName ?? '全公司' },
          { title: '工程', value: (r) => r.projectName ?? '不限' },
          { title: '费用类型', value: (r) => EXPENSE_TYPE_LABELS[r.expenseType] ?? r.expenseType },
          { title: '原始核定(元)', value: (r) => (r.amountCents / 100).toFixed(2) },
          { title: '已批准调整(元)', value: (r) => (r.adjustCents / 100).toFixed(2) },
          { title: '当前预算(元)', value: (r) => (r.currentCents / 100).toFixed(2) },
          { title: '已用(元)', value: (r) => (r.usedCents / 100).toFixed(2) },
          { title: '在途(元)', value: (r) => (r.pendingCents / 100).toFixed(2) },
          {
            title: '执行率',
            value: (r) => (r.usedRatio === null ? '' : `${(r.usedRatio * 100).toFixed(1)}%`),
          },
        ]}
        columns={columns}
        dataSource={list.list}
        rowKey="id"
        loading={list.loading}
        error={list.error}
        isFallback={list.isFallback}
        fallbackReason={list.fallbackReason}
        onRetry={list.reload}
        pagination={list.pagination}
        onChange={list.handleTableChange}
        scrollX={1500}
        emptyText="当前期间还没有预算。点右上角「编制预算」开始。"
        toolbar={
          <Space wrap>
            <Select
              placeholder="周期"
              style={{ width: 100 }}
              value={periodType}
              onChange={(v) => {
                setPeriodType(v);
                list.setQuery({ periodType: v, period: currentPeriod(v) });
              }}
              options={Object.entries(BUDGET_PERIOD_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <DatePicker
              picker={periodType === 'MONTH' ? 'month' : 'year'}
              value={list.query.period ? dayjs(list.query.period, periodType === 'MONTH' ? 'YYYY-MM' : 'YYYY') : null}
              onChange={(d) =>
                list.setQuery({
                  period: d ? d.format(periodType === 'MONTH' ? 'YYYY-MM' : 'YYYY') : undefined,
                })
              }
              allowClear
            />
            <Select
              allowClear
              placeholder="费用类型"
              style={{ width: 130 }}
              value={list.query.expenseType}
              onChange={(v) => list.setQuery({ expenseType: v })}
              options={Object.entries(EXPENSE_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Select
              allowClear
              placeholder="状态"
              style={{ width: 110 }}
              value={list.query.status}
              onChange={(v) => list.setQuery({ status: v })}
              options={Object.entries(BUDGET_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Button onClick={list.resetQuery}>重置</Button>
          </Space>
        }
      />

      {/* —— 编制预算 —— */}
      <Modal
        open={createModal}
        title="编制预算"
        onCancel={() => setCreateModal(false)}
        onOk={() => void saveCreate()}
        confirmLoading={saving}
        destroyOnHidden
        okText="保存"
        width={640}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="同一「期间 + 部门 + 费用类型 + 工程」只能有一条预算"
          description="重复编制会被拒绝，请直接编辑已有的那条（未发生支出时）或走调整流程。"
        />
        <Form
          key="create"
          form={createForm}
          layout="vertical"
          preserve={false}
          initialValues={{ periodType: BudgetPeriodType.MONTH, expenseType: ExpenseType.TRAVEL }}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="periodType" label="周期" rules={[{ required: true }]}>
                <Select
                  options={Object.entries(BUDGET_PERIOD_TYPE_LABELS).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="period" label="期间" extra="留空取当前期间">
                <DatePicker picker="month" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="deptId" label="部门" extra="留空 = 全公司预算">
                <Select
                  allowClear
                  placeholder="全公司"
                  loading={depts.loading}
                  options={(depts.data ?? []).map((d) => ({ value: d.id, label: d.name }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="expenseType" label="费用类型" rules={[{ required: true }]}>
                <Select
                  options={Object.entries(EXPENSE_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="amountYuan" label="预算金额（元）" rules={[{ required: true, message: '请填写金额' }]}>
            <InputNumber style={{ width: '100%' }} precision={2} min={0} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>

      {/* —— 预算调整 —— */}
      <Modal
        open={adjustTarget !== null}
        title={`预算调整：${adjustTarget?.period ?? ''} ${adjustTarget?.expenseType ?? ''}`}
        onCancel={() => setAdjustTarget(null)}
        onOk={() => void saveAdjust()}
        confirmLoading={saving}
        destroyOnHidden
        okText="提交调整"
        width={560}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`当前预算 ¥${((adjustTarget?.currentCents ?? 0) / 100).toFixed(2)}（原始 ${((adjustTarget?.amountCents ?? 0) / 100).toFixed(2)} + 已批准调整 ${((adjustTarget?.adjustCents ?? 0) / 100).toFixed(2)}）`}
          description="调整单需要审批。提交后只是「待批」，批准后才计入当前预算——所以列表里的「当前预算」不会立刻变。"
        />
        <Form key={adjustTarget?.id ?? 'adj'} form={adjustForm} layout="vertical" preserve={false}>
          <Form.Item
            name="deltaYuan"
            label="调整金额（元）"
            extra="增额填正数，减额填负数"
            rules={[{ required: true, message: '请填写调整金额' }]}
          >
            <InputNumber style={{ width: '100%' }} precision={2} />
          </Form.Item>
          <Form.Item name="reason" label="调整原因" rules={[{ required: true, message: '请填写原因' }]}>
            <Input.TextArea rows={3} maxLength={500} placeholder="如：业务量增加，追加预算" />
          </Form.Item>
        </Form>
      </Modal>

      {/* —— 删除预算 —— */}
      <ConfirmDanger
        open={deleteTarget !== null}
        title={`删除预算：${deleteTarget?.period ?? ''} ${deleteTarget?.expenseType ?? ''}`}
        description={
          <span>
            已发生支出或在途占用的预算<strong>删不掉</strong>——删了那些支出会变成"没有预算的费用"，
            执行报表凭空少一块。那种情况请走「调整」把它调减到 0。
          </span>
        }
        level="high"
        reasonLabel="删除原因（必填，将写入操作日志）"
        minReasonLength={4}
        confirmText="确认删除"
        onConfirm={(reason) => void saveDelete(reason)}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* —— 调整记录（逐条批准/驳回） —— */}
      <Modal
        open={adjustListTarget !== null}
        title={`调整记录：${adjustListTarget?.period ?? ''} ${adjustListTarget?.expenseType ?? ''}`}
        onCancel={() => setAdjustListTarget(null)}
        footer={
          <Button onClick={() => setAdjustListTarget(null)}>
            关闭
          </Button>
        }
        width={760}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="逐条批准，不做「一键全批」"
          description="调整是**增量累加**的，批错一条就直接改数字，而且重复批准会被数据库挡住。所以必须一条一条来，这样「某一条为什么不批」也表达得出来。"
        />
        {adjusts.loading ? (
          <div style={{ padding: 16, textAlign: 'center' }}>加载中…</div>
        ) : (adjusts.data ?? []).length === 0 ? (
          <Empty description="这条预算还没有调整记录" />
        ) : (
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={adjusts.data ?? []}
            columns={[
              {
                title: '调整金额',
                dataIndex: 'deltaCents',
                width: 120,
                align: 'right' as const,
                onCell: () => ({ style: moneyCellStyle }),
                render: (v: number) => (
                  <span style={{ color: v < 0 ? '#cf1322' : '#389e0d' }}>
                    <Money cents={v} colorNegative={false} />
                  </span>
                ),
              },
              { title: '原因', dataIndex: 'reason', ellipsis: true },
              { title: '提交人', dataIndex: 'createdByName', width: 90, render: (v: string | null) => v ?? '-' },
              {
                title: '状态',
                dataIndex: 'status',
                width: 90,
                render: (v: string) => (
                  <Tag color={v === 'APPROVED' ? 'success' : v === 'REJECTED' ? 'error' : 'processing'}>
                    {v === 'APPROVED' ? '已批准' : v === 'REJECTED' ? '已驳回' : '待审批'}
                  </Tag>
                ),
              },
              {
                title: '操作',
                key: 'act',
                width: 130,
                render: (_: unknown, row: { id: string; status: string }) =>
                  row.status === 'PENDING' && canManage ? (
                    <Space size={4}>
                      <Button
                        size="small"
                        type="link"
                        onClick={() => void decideAdjustment(row.id, true)}
                      >
                        批准
                      </Button>
                      <Button
                        size="small"
                        type="link"
                        danger
                        onClick={() => void decideAdjustment(row.id, false)}
                      >
                        驳回
                      </Button>
                    </Space>
                  ) : null,
              },
            ]}
          />
        )}
      </Modal>
    </div>
  );
}

export default ExpenseBudgetPage;
